import { max, every, isNumber } from 'lodash';

import {
    SET_BLACKLIST,
    SET_WHITELIST,
    SET_SPEECH_RATE,
    SET_SPEECH_RATIO,
    SET_LOUDNESS,
    SET_IS_STARTED,
    SET_ELAPSED_SECONDS,
    SET_CALL_RESULT,
    SET_RESULT_POPUP_VISIBILITY,
    SET_RESULT_POPUP_VISIBILITY_ON_LOAD,
    RELOAD,
    UPDATE_RELOAD_COUNT_DOWN,
    SHOW_NOTIFICATION,
    HIDE_NOTIFICATION,
    SET_USER_INTERFACE_CONFIG,
    HIDE_NEW_CALL_MESSAGE,
    SET_MANUAL_BUTTON_AVAILABILITY,
    SET_SNOOZE_TIMER_SUCCESS,
    SET_SNOOZE_TIMER_FAIL,
    SNOOZE_TRAINER,
    ADD_SNOOZE_TIMER,
    DEVICE_LIST_RECEIVE_ERROR,
    CONFIGURATION_LOADED,
    SET_ASR_CONFIG_ERRORS,
    MANUAL_OPT_IN,
    MANUAL_OPT_OUT,
    MANUAL_OPT_STOP,
    CALL_HOLD,
    CALL_UNHOLD,
    SET_INFINITY_SNOOZE,
    RESUME_TRAINER,
    SEND_MANUAL_CALL_EVENT,
    DEVICE_LIST_RECEIVED,
    SET_SPEECH_PACE,
    SET_NEW_SPEECH_RATE,
    SAVE_CONVERSION,
    SET_RECORD_CALL,
    EVENT_OPT_IN,
    EVENT_OPT_OUT,
    EVENT_OPT_STOP,
    OPT_RESET,
    CALL_NOTIFICATION,
    SET_PHRASE_INTEGRATION_WORDS,
    MIC_DEVICE_NOT_AVAILABLE
} from '../../actions/actionTypes/trainer';

import { SET_USER_SCORES_SUCCESS } from '../../actions/actionTypes/user';
import { USER_PROFILE_FETCH_FAIL } from '../../actions/actionTypes/userProfile';
import { DOMAIN_CALL_STARTED, DOMAIN_DAEMON_VERSION } from '../../actions/actionTypes/domain';
import { calculateScore } from '../../../utils/points';
import errorCodes from '../../../utils/errorCodes';
import { compareVersions } from '../../../utils/versions';

import { HIDE_CONVERSION_MODAL, SHOW_CONVERSION_MODAL } from '../../actions/actionTypes/conversion';
import type {
    AudioDeviceInfo,
    KeyPhraseIntegrationWords,
    TrainerAsrConfigErrors,
    TrainerPhraseGroup,
    TrainerState,
    TrainerUIConfig
} from '../../types';

export const initialState: TrainerState = {
    daemonVersion: '',
    configLoaded: false,
    whitelist: [],
    blacklist: [],
    speechRate: null,
    newSpeechRate: null,
    speechPace: null,
    speechRatio: null,
    loudness: null,
    isStarted: false,
    elapsedSeconds: 0,
    showNewCallMessage: false,
    showCallEndMessage: false,
    whitelistPhrasesSpottingScore: null,
    lastCallPhraseSpottingScore: null,
    isCallResultVisible: false,
    isLastCallResult: false,
    isSoundContinued: false,
    isReloading: false,
    errorCode: null,
    countdown: 0,
    isNotificationShown: false,
    systemSoundRecognition: false,
    showTranscript: true,
    userInterfaceConfig: {
        displayTranscript: false,
        displayTranslator: false,
        displayAssistantChat: false,
        assistantChatProjectId: '',
        requirePersonalUsername: false,
        displaySnoozingControls: false,
        displayOnlineButton: false,
        displayCallOptOutControls: false,
        showDashboardLink: false,
        enableCallSimulator: false,
        moveSaidDoSayWordsToBottom: false,
        displayGamificationPerCall: true,
        displayGamificationWeekly: true,
        displayRecordingState: false,
        displayNewSpeechRate: false
    },
    enableTrainer: false,
    enableSales: false,
    enableEvaluations: false,
    enableCallSimulator: false,
    bonusScore: 0,
    silenceDetectionThreshold: 0,
    suggestionCallFrequency: 0,
    isTrainerActive: true,
    isManualCallButtonEnabled: true,
    snoozeTimer: null,
    asrConfigErrors: {},
    optInOn: true,
    isOptOut: false,
    stopRecordingCustomer: false,
    callHold: false,
    deviceList: {
        mics: [],
        speakers: []
    },
    micDeviceNotAvailable: false,
    isCallEnded: false,
    callDuration: 0,
    callId: null,
    recordCall: true,
    conversionModalOpen: false,
    callEventNotification: null,
    keyPhraseIntegrationWords: null
};

const roundToParticularSign = (value: unknown, signs = 0) => {
    return isNumber(value) && typeof value === 'number' ? Math.round(value * 10 ** signs) / 10 ** signs : null;
};

interface WhitelistItem {
    count?: number;
    alternatives: { count?: number; points?: number }[];
}

interface TrainerPayload {
    asrConfigErrors?: TrainerAsrConfigErrors;
    whitelist?: TrainerPhraseGroup[];
    blacklist?: TrainerPhraseGroup[];
    isImmediate?: boolean;
    countdown?: number;
    code?: number;
    userInterfaceConfig?: TrainerUIConfig;
    mics?: AudioDeviceInfo[];
    speakers?: AudioDeviceInfo[];
    callId?: string | number | null;
}

interface ReducerPayload extends TrainerPayload {
    isStarted?: boolean;
    systemSoundRecognition?: boolean;
    elapsedSeconds?: number;
    loudness?: number;
    speechRatio?: number;
    speechPace?: number;
    newSpeechRate?: number;
    speechRate?: number;
    isLastCallResult?: boolean;
    keyPhraseIntegrationWords?: unknown;
    isCallResultVisible?: boolean;
    bonusScore?: number;
    silenceDetectionThreshold?: number;
    suggestionCallFrequency?: number;
    enableTrainer?: boolean;
    enableSales?: boolean;
    enableEvaluations?: boolean;
    enableCallSimulator?: boolean;
    daemonVersion?: string;
    snoozeFor?: number;
    isAvailable?: boolean;
    recordCall?: boolean;
    remove?: boolean;
    eventName?: string;
}

function toPhraseGroups(value: unknown): TrainerPhraseGroup[] {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.filter((item): item is TrainerPhraseGroup => Boolean(item) && typeof item === 'object');
}

function isAudioDeviceInfo(value: unknown): value is AudioDeviceInfo {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return false;
    }

    const entries = Object.values(value as Record<string, unknown>);
    return entries.length > 0 && entries.every(item => item === null || ['string', 'number', 'boolean'].includes(typeof item));
}

function toAudioDeviceList(value: unknown): AudioDeviceInfo[] {
    return Array.isArray(value) ? value.filter(isAudioDeviceInfo) : [];
}

function toKeyPhraseIntegrationWords(value: unknown): KeyPhraseIntegrationWords | null {
    if (!value || typeof value !== 'object') {
        return null;
    }
    const candidate = value as { start?: unknown; stop?: unknown };
    const start = Array.isArray(candidate.start) ? candidate.start.filter((item): item is string => typeof item === 'string') : undefined;
    const stop = Array.isArray(candidate.stop) ? candidate.stop.filter((item): item is string => typeof item === 'string') : undefined;
    if ((!start || start.length === 0) && (!stop || stop.length === 0)) {
        return null;
    }
    return { start, stop };
}

function normalizeAsrConfigErrors(value: unknown): TrainerAsrConfigErrors {
    if (!value || typeof value !== 'object') {
        return {};
    }
    const candidate = value as { noWhitelist?: unknown; noBlacklist?: unknown; noTeam?: unknown };
    return {
        noWhitelist: typeof candidate.noWhitelist === 'boolean' ? candidate.noWhitelist : undefined,
        noBlacklist: typeof candidate.noBlacklist === 'boolean' ? candidate.noBlacklist : undefined,
        noTeam: typeof candidate.noTeam === 'boolean' ? candidate.noTeam : undefined
    };
}

const getWhitelistScore = (whitelist: WhitelistItem[], bonusScore: number) => {
    const totalScore = whitelist.reduce((acc, item) => {
        return acc + (max(item.alternatives.map(alt => alt.points)) || 0);
    }, 0);

    return {
        spottedPhrasesScore: calculateScore(
            whitelist.map(item => ({
                count: typeof item.count === 'number' ? item.count : -1,
                alternatives: item.alternatives.map(alt => ({
                    count: typeof alt.count === 'number' ? alt.count : -1,
                    points: Number(alt.points) || 0
                }))
            }))
        ),
        totalPhrasesScore: totalScore,
        bonusScore: every(whitelist, 'count') ? bonusScore : 0
    };
};

function toWhitelistItems(value: unknown): WhitelistItem[] {
    return toPhraseGroups(value).map(item => {
        const alternatives = Array.isArray(item.alternatives) ? item.alternatives : [];
        return {
            count: typeof item.count === 'number' ? item.count : undefined,
            alternatives: alternatives.map(alt => {
                return {
                    count: typeof alt.count === 'number' ? alt.count : undefined,
                    points: typeof alt.points === 'number' ? alt.points : undefined
                };
            })
        };
    });
}

const updateScore = (state: TrainerState, newWhitelist: WhitelistItem[]) => {
    if (state.isStarted || !state.whitelistPhrasesSpottingScore) {
        return getWhitelistScore(newWhitelist, state.bonusScore);
    }
    return state.whitelistPhrasesSpottingScore;
};

const clearScore = (scores: TrainerState['whitelistPhrasesSpottingScore']) => {
    return {
        ...scores,
        bonusScore: 0,
        spottedPhrasesScore: 0
    };
};

interface ReducerAction {
    type: string;
    payload?: ReducerPayload;
}

const trainer = (state: TrainerState = initialState, action: ReducerAction): TrainerState => {
    const { type } = action;
    const payload = action.payload || {};
    const trainerPayload: TrainerPayload = payload;
    switch (type) {
        case CONFIGURATION_LOADED: {
            return {
                ...state,
                configLoaded: true
            };
        }

        case SET_ASR_CONFIG_ERRORS: {
            return {
                ...state,
                asrConfigErrors: normalizeAsrConfigErrors(trainerPayload.asrConfigErrors)
            };
        }
        case USER_PROFILE_FETCH_FAIL: {
            return initialState;
        }
        case SET_IS_STARTED: {
            const isStarted = Boolean(payload.isStarted);
            const systemSoundRecognition = Boolean(payload.systemSoundRecognition);

            return {
                ...state,
                isStarted,
                isSoundContinued: false,
                showNewCallMessage: isStarted,
                showCallEndMessage: !isStarted,
                systemSoundRecognition: isStarted ? systemSoundRecognition : state.systemSoundRecognition,
                whitelistPhrasesSpottingScore: isStarted
                    ? clearScore(state.whitelistPhrasesSpottingScore)
                    : state.whitelistPhrasesSpottingScore,
                optInOn: isStarted ? false : state.optInOn,
                isOptOut: false,
                callHold: false
            };
        }

        case SHOW_CONVERSION_MODAL: {
            return { ...state, conversionModalOpen: true };
        }

        case HIDE_CONVERSION_MODAL: {
            return { ...state, conversionModalOpen: false };
        }

        case HIDE_NEW_CALL_MESSAGE: {
            return { ...state, showNewCallMessage: false };
        }

        case SET_ELAPSED_SECONDS: {
            return { ...state, elapsedSeconds: Number(payload.elapsedSeconds) || 0 };
        }
        case SET_LOUDNESS: {
            return { ...state, loudness: roundToParticularSign(payload.loudness) };
        }
        case SET_SPEECH_RATIO: {
            return {
                ...state,
                speechRatio: roundToParticularSign(payload.speechRatio, 1)
            };
        }
        case SET_SPEECH_PACE: {
            return {
                ...state,
                speechPace: roundToParticularSign(payload.speechPace)
            };
        }
        case SET_NEW_SPEECH_RATE: {
            return {
                ...state,
                newSpeechRate: roundToParticularSign(payload.newSpeechRate)
            };
        }
        case SET_SPEECH_RATE: {
            return {
                ...state,
                speechRate: roundToParticularSign(payload.speechRate)
            };
        }
        case SET_WHITELIST: {
            const whitelist = toPhraseGroups(trainerPayload.whitelist);
            const whitelistForScore = toWhitelistItems(trainerPayload.whitelist);

            return {
                ...state,
                whitelist,
                whitelistPhrasesSpottingScore: updateScore(state, whitelistForScore)
            };
        }
        case SET_BLACKLIST: {
            return { ...state, blacklist: toPhraseGroups(trainerPayload.blacklist) };
        }
        case SET_CALL_RESULT: {
            return {
                ...state,
                whitelist: toPhraseGroups(trainerPayload.whitelist),
                lastCallPhraseSpottingScore: {
                    ...state.whitelistPhrasesSpottingScore
                },
                isLastCallResult: Boolean(payload.isLastCallResult),
                isCallResultVisible: Boolean(state.userInterfaceConfig.displayGamificationPerCall)
            };
        }
        case SET_USER_SCORES_SUCCESS: {
            return {
                ...state,
                whitelistPhrasesSpottingScore: clearScore(state.whitelistPhrasesSpottingScore)
            };
        }
        case SET_PHRASE_INTEGRATION_WORDS: {
            return {
                ...state,
                keyPhraseIntegrationWords: toKeyPhraseIntegrationWords(payload.keyPhraseIntegrationWords)
            };
        }
        case SET_RESULT_POPUP_VISIBILITY: {
            return { ...state, isCallResultVisible: Boolean(payload.isCallResultVisible) };
        }
        case SET_RESULT_POPUP_VISIBILITY_ON_LOAD: {
            return {
                ...state,
                isCallResultVisible: state.isCallResultVisible && state.isLastCallResult && state.recordCall
            };
        }
        case RELOAD: {
            if (!trainerPayload.isImmediate) {
                return {
                    ...state,
                    isReloading: true,
                    countdown: Number(trainerPayload.countdown) || 0,
                    errorCode: trainerPayload.code || null
                };
            }

            return state;
        }
        case UPDATE_RELOAD_COUNT_DOWN: {
            return { ...state, countdown: Number(payload.countdown) || 0 };
        }

        case SHOW_NOTIFICATION: {
            return { ...state, isNotificationShown: true };
        }

        case HIDE_NOTIFICATION: {
            return { ...state, isNotificationShown: false };
        }
        case SET_USER_INTERFACE_CONFIG: {
            return {
                ...state,
                userInterfaceConfig: {
                    ...state.userInterfaceConfig,
                    ...(trainerPayload.userInterfaceConfig || {})
                },
                bonusScore: Number(payload.bonusScore) || 0,
                silenceDetectionThreshold: Number(payload.silenceDetectionThreshold) || 0,
                suggestionCallFrequency: Number(payload.suggestionCallFrequency) || 0,
                enableTrainer: Boolean(payload.enableTrainer),
                enableSales: Boolean(payload.enableSales),
                enableEvaluations: Boolean(payload.enableEvaluations),
                enableCallSimulator: Boolean(payload.enableCallSimulator)
            };
        }
        case DOMAIN_DAEMON_VERSION: {
            const daemonVersion = String(payload.daemonVersion || '');
            const { showDashboardLink } = state.userInterfaceConfig;
            const isAvailableVersion = compareVersions(daemonVersion, '1.8.66');
            return {
                ...state,
                daemonVersion,
                userInterfaceConfig: {
                    ...state.userInterfaceConfig,
                    showDashboardLink: showDashboardLink && isAvailableVersion
                }
            };
        }
        case SET_SNOOZE_TIMER_SUCCESS: {
            return {
                ...state,
                isTrainerActive: false,
                snoozeTimer: Number(payload.snoozeFor) || 0
            };
        }
        case SET_SNOOZE_TIMER_FAIL: {
            return {
                ...state,
                errorCode: errorCodes.user.PATCH_USER_PROFILE_FAILED
            };
        }
        case RESUME_TRAINER: {
            return state;
        }
        case SET_INFINITY_SNOOZE: {
            return state;
        }
        case SNOOZE_TRAINER: {
            return {
                ...state,
                errorCode: initialState.errorCode
            };
        }
        case ADD_SNOOZE_TIMER: {
            return {
                ...state,
                errorCode: initialState.errorCode
            };
        }
        case SET_MANUAL_BUTTON_AVAILABILITY: {
            return {
                ...state,
                isManualCallButtonEnabled: Boolean(payload.isAvailable)
            };
        }
        case SET_RECORD_CALL: {
            return {
                ...state,
                recordCall: Boolean(payload.recordCall)
            };
        }
        case DEVICE_LIST_RECEIVE_ERROR: {
            return {
                ...state,
                deviceListError: true
            };
        }

        case MIC_DEVICE_NOT_AVAILABLE: {
            return {
                ...state,
                micDeviceNotAvailable: true
            };
        }

        case DEVICE_LIST_RECEIVED: {
            return {
                ...state,
                deviceList: {
                    mics: toAudioDeviceList(trainerPayload.mics),
                    speakers: toAudioDeviceList(trainerPayload.speakers)
                }
            };
        }
        case MANUAL_OPT_IN: {
            return {
                ...state,
                optInOn: true,
                isOptOut: false,
                stopRecordingCustomer: false
            };
        }
        case MANUAL_OPT_STOP: {
            return {
                ...state,
                optInOn: false,
                isOptOut: false,
                stopRecordingCustomer: true
            };
        }
        case MANUAL_OPT_OUT: {
            return {
                ...state,
                optInOn: false,
                isOptOut: true,
                stopRecordingCustomer: true
            };
        }

        case EVENT_OPT_IN: {
            return {
                ...state,
                optInOn: true,
                isOptOut: false,
                stopRecordingCustomer: false
            };
        }
        case EVENT_OPT_OUT: {
            return {
                ...state,
                optInOn: false,
                isOptOut: true,
                stopRecordingCustomer: true
            };
        }
        case EVENT_OPT_STOP: {
            return {
                ...state,
                optInOn: false,
                isOptOut: false,
                stopRecordingCustomer: true
            };
        }

        case OPT_RESET: {
            return {
                ...state,
                optInOn: true,
                isOptOut: false,
                stopRecordingCustomer: false
            };
        }

        case DOMAIN_CALL_STARTED: {
            return {
                ...state,
                callId: trainerPayload.callId || null
            };
        }

        case SAVE_CONVERSION: {
            return state;
        }

        case CALL_HOLD: {
            return {
                ...state,
                callHold: true
            };
        }
        case CALL_UNHOLD: {
            return {
                ...state,
                callHold: false
            };
        }
        case SEND_MANUAL_CALL_EVENT: {
            return state;
        }
        case CALL_NOTIFICATION: {
            if (payload.remove) {
                return {
                    ...state,
                    callEventNotification: null
                };
            }

            if (payload.eventName && !state.callEventNotification) {
                return {
                    ...state,
                    callEventNotification: String(payload.eventName)
                };
            }

            return state;
        }

        default: {
            return state;
        }
    }
};

export default trainer;
